import { z } from 'zod';
import { DoughnutChart } from '@/components/charts/DoughnutChart';
import { doughnutChartConfigSchema } from '@/components/charts/chartConfig';
import { SubTitle, TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { NARROW_WIDGET_LAYOUT_BASE_CLASS, useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema } from '@/lib/fontStyle';
import { substituteVariables, textReplacementSchema, type TextReplacement } from '@/lib/text';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A half-ring "workspace status" gauge with a title, a status subtitle and a sentence of
 * `{{variable}}` text. Port of `doughnutWidget/DoughnutWidget.vue`.
 *
 * Used by four deployed configs (`corio`, `freshwaves`, `hbr_tcc_2`, `hbr_tcc_3`), all with
 * the same shape: a 180° arc rotated -90°, an 80% cutout and a middle-text label.
 */

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  /**
   * The payload's `subtitle` is a **key into this map**, not a string to render. The four
   * deployed configs map `"1"` and `"2"` to `SAFE FOR WORK` / `CONSIDER WORKING FROM HOME`.
   */
  subtitles: z.record(z.string(), z.string()).optional(),
  /** [sic] lowercase `f`, unlike `titleFont` and `textBlocFont` beside it. */
  subtitlefont: fontOptionsSchema.optional(),
  textBloc: z
    .object({
      text: z.string().default(''),
      /** The spelling `DoughnutWidget.vue` reads. `corio` is the config that carries it. */
      replace: z.array(textReplacementSchema).optional(),
      /** [sic] The spelling the other three configs carry — see `resolveText`. */
      replaced: z.array(textReplacementSchema).optional(),
    })
    .prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  chartConfig: doughnutChartConfigSchema.prefault({}),
});

/** docs/MIGRATION-WIDGETS.md §7. Everything is optional; the empty state covers the rest. */
const payloadSchema = z.looseObject({
  values: z.array(z.number()).optional(),
  chartMiddleText: z.string().optional(),
  subtitle: z.string().optional(),
});

type DoughnutPayload = z.infer<typeof payloadSchema>;

/** docs/MIGRATION-WIDGETS.md §9: a two-segment ring, 0 present out of 1, before any message. */
const EMPTY_VALUES = [0, 1];

export function DoughnutWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  /**
   * The one widget that keeps its legacy base className (MIGRATION decision #18).
   *
   * Its two halves are both `lg:w-6/12`, so the canonical class's `1.5rem` gutter takes the row
   * to `100% + 1.5rem` and its `flex-wrap` then drops the chart onto a second line *underneath
   * its own title*. Measured in Chrome at 1920px against `corio`: two 867px columns in a
   * 1734.67px row with a 33px gap. The legacy app renders them side by side, and so does this.
   */
  const { className, style } = useWidgetLayout(opts?.layout, {
    baseClass: NARROW_WIDGET_LAYOUT_BASE_CLASS,
  });

  if (!parsed.success) {
    warnOnce(
      `doughnut-widget-options:${componentId}`,
      '[signage] DoughnutWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const payload = readPayload(data, componentId);

  // `values` is the one field with a non-empty fallback: an empty ring would render as a
  // single grey arc, where `[0, 1]` renders the same shape the live chart has.
  const values = payload?.values ?? EMPTY_VALUES;
  const chartMiddleText = payload?.chartMiddleText ?? '';
  const subtitle = resolveSubtitle(opt.subtitles, payload?.subtitle);
  const textBlock = resolveText(opt.textBloc, payload);

  return (
    <div className={className} style={style}>
      <TitleColumn className="w-full lg:w-6/12" title={opt.title} titleFont={opt.titleFont}>
        <SubTitle text={subtitle} font={opt.subtitlefont} />
        {/* The legacy `v-if="textBlock"` wrapper. `TextBloc` renders nothing for an empty
            string on its own, but the extra div is part of the DOM being matched. */}
        {textBlock ? (
          <div>
            <TextBloc text={textBlock} font={opt.textBlocFont} />
          </div>
        ) : null}
      </TitleColumn>
      <div className="w-full lg:w-6/12 flex justify-center">
        {/*
         * `w-full max-w-96` rather than the legacy `w-96`. `w-96` is 24rem, so it scales with
         * the root font size and is not a fixed *pixel* width — but it is a fixed width in a
         * percentage container, and a container narrower than 24rem gets a chart wider than
         * itself, which is horizontal overflow on a screen that cannot scroll. At every viewport
         * where the container is the wider of the two — which is every viewport any display has,
         * in both hardware generations — the two render identically, because `max-width` is then
         * what decides.
         */}
        <div className="w-full max-w-96 pt-3 lg:pt-0">
          <DoughnutChart
            values={values}
            chartMiddleText={chartMiddleText}
            config={opt.chartConfig}
            className="col-span-1 md:col-span-2 my-4 md:my-0"
          />
        </div>
      </div>
    </div>
  );
}

/** The payload, or `undefined` before the first message / on a shape mismatch. */
function readPayload(data: unknown, componentId: string): DoughnutPayload | undefined {
  if (data === undefined) return undefined;

  const payload = payloadSchema.safeParse(data);
  if (!payload.success) {
    warnOnce(
      `doughnut-widget-payload:${componentId}`,
      '[signage] DoughnutWidget payload rejected; rendering the empty state. %s',
      z.prettifyError(payload.error),
    );
    return undefined;
  }
  return payload.data;
}

/**
 * `payload.subtitle` is a key; this resolves it against `options.subtitles`.
 *
 * A missing key, a missing map or a missing payload all render nothing, exactly as the legacy
 * three-level `if` did.
 */
function resolveSubtitle(
  subtitles: Record<string, string> | undefined,
  key: string | undefined,
): string {
  if (!key || !subtitles) return '';
  return subtitles[key] ?? '';
}

/**
 * The `{{variable}}` sentence, or `''` before the first message.
 *
 * Two things worth being explicit about, both of which follow decisions SYM-495 already made
 * for `TextWidget`:
 *
 * **The empty state is `''`.** The legacy guard is `if (options.textBloc.text && data.value)`
 * and Vuex initialised `data` to a truthy `{}`, so four displays currently render *"There are
 * N/A employees on this floor. Capacity is set to N/A people."* on a dead feed. The store here
 * returns `undefined` until the first message, so the same guard renders nothing —
 * docs/MIGRATION-WIDGETS.md §8, W-18, and §9.
 *
 * **Both `replace` and `replaced` are read.** `DoughnutWidget.vue` reads only `replace`, and
 * only `corio` spells it that way; `freshwaves`, `hbr_tcc_2` and `hbr_tcc_3` all spell it
 * `replaced`, so their *"are <strong>1</strong> employees"* -> *"is <strong>1</strong>
 * employee"* fix-up has never run. This is the same W-15 shape SYM-495 hit on `TextWidget`,
 * on a different widget and three more configs, and it is a visible change on those three
 * whenever exactly one person is on the floor.
 */
function resolveText(
  textBloc: { text: string; replace?: TextReplacement[]; replaced?: TextReplacement[] },
  payload: DoughnutPayload | undefined,
): string {
  if (!textBloc.text || payload === undefined) return '';

  const replace: readonly TextReplacement[] | undefined = textBloc.replace ?? textBloc.replaced;
  return substituteVariables(textBloc.text, payload, { missing: 'na', replace });
}
